/**
 * The invoice attachment — the timesheet you send with a bill.
 *
 * Every option from the standalone tracker is here: three presets, per-field
 * toggles, the month / since / all period shapes, a project filter and a
 * billable-only switch. The default preset is deliberately HOURS ONLY: this
 * document accompanies an invoice, it is not one, and a client who is sent
 * rates on an attachment will negotiate with the attachment.
 *
 * WHAT DIFFERS FROM THE TRACKER is how the PDF is made. The tracker draws one
 * with jsPDF + autotable; Project Hub has neither dependency and already makes
 * its client report a PDF through the browser's own print dialog, so this does
 * the same. You get a typeset HTML page and "Save as PDF" from the print
 * sheet — which also means what you see on screen is exactly what prints,
 * rather than a preview of a separate rendering.
 *
 * Money comes from each entry's `rateSnapshot`, so re-printing an old month
 * after a rate change reproduces what was billed at the time.
 *
 * MARKING AS INVOICED is the step that closes the loop. Once the bill has gone
 * out from the accounting program, the entries it covered are stamped here
 * (with that program's invoice number, if you have it), and the default
 * "Not yet invoiced" filter stops offering them — so next month's attachment
 * starts from what is actually still owed a bill.
 */

import { useMemo, useState } from "react";

import { card } from "@/components/atoms";
import { useToast } from "@/context/ToastContext";
import { useAllItems, useProjects } from "@/hooks/useData";
import { useClients, useTimeEntries, useTimeMutations } from "@/hooks/useTime";
import { ReportFooter, ReportMasthead, reportPaper, reportTable, reportTd, reportTh } from "@/components/ReportMasthead";
import { deriveEntryLabel, entryDayKey, entryKind, hours, isUnbilled, money } from "@/lib/timeRollup";
import type { Client, Project, TimeEntry } from "@/lib/types";

type Preset = "attachment" | "detailed" | "summary";
type PeriodMode = "month" | "since" | "all";

interface Fields {
  businessDetails: boolean;
  clientDetails: boolean;
  date: boolean;
  project: boolean;
  description: boolean;
  activity: boolean;
  duration: boolean;
  rate: boolean;
  amount: boolean;
  subtotals: boolean;
  footerNote: boolean;
}

const PRESETS: Record<Preset, { label: string; hint: string; fields: Fields }> = {
  attachment: {
    label: "Invoice attachment",
    hint: "Hours only — what the invoice bills for, without restating the rates.",
    fields: {
      businessDetails: true, clientDetails: true, date: true, project: true,
      description: true, activity: false, duration: true,
      rate: false, amount: false, subtotals: true, footerNote: true,
    },
  },
  detailed: {
    label: "Detailed log",
    hint: "Every column, including rates and line amounts.",
    fields: {
      businessDetails: true, clientDetails: true, date: true, project: true,
      description: true, activity: true, duration: true,
      rate: true, amount: true, subtotals: true, footerNote: true,
    },
  },
  summary: {
    label: "Project summary",
    hint: "One line per project — no dates, no descriptions.",
    fields: {
      businessDetails: true, clientDetails: true, date: false, project: true,
      description: false, activity: false, duration: true,
      rate: true, amount: true, subtotals: true, footerNote: true,
    },
  },
};

const FIELD_LABELS: [keyof Fields, string][] = [
  ["businessDetails", "Business details"],
  ["clientDetails", "Client and period"],
  ["date", "Date column"],
  ["project", "Project column"],
  ["description", "Description column"],
  ["activity", "Activity column"],
  ["duration", "Hours column"],
  ["rate", "Rate column"],
  ["amount", "Amount column"],
  ["subtotals", "Totals line"],
  ["footerNote", "Footer note"],
];

export function InvoiceExport({ onClose }: { onClose: () => void }) {
  const { data: entries = [] } = useTimeEntries();
  const { data: projects = [] } = useProjects();
  const { data: clients = [] } = useClients();
  const { data: items = [] } = useAllItems();
  const toast = useToast();

  const [preset, setPreset] = useState<Preset>("attachment");
  const [fields, setFields] = useState<Fields>(PRESETS.attachment.fields);
  const [projectId, setProjectId] = useState("all");
  const [billableOnly, setBillableOnly] = useState(true);
  const [unbilledOnly, setUnbilledOnly] = useState(true);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [marking, setMarking] = useState(false);
  const { setInvoiced } = useTimeMutations();
  const [periodMode, setPeriodMode] = useState<PeriodMode>("month");

  /** Months that actually have entries — an empty month is not worth offering. */
  const months = useMemo(() => {
    const seen = new Set(entries.map((e) => entryDayKey(e).slice(0, 7)));
    return [...seen].sort().reverse().map((value) => ({
      value,
      label: new Date(`${value}-01T00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" }),
    }));
  }, [entries]);
  const [month, setMonth] = useState(() => months[0]?.value ?? new Date().toISOString().slice(0, 7));
  const [since, setSince] = useState(() => new Date().toISOString().slice(0, 10));

  const itemTitle = useMemo(() => new Map(items.map((i) => [i.id, i.title])), [items]);

  // Both period tests are lexical comparisons on YYYY-MM-DD, which sorts
  // chronologically — no parsing, and no timezone to get wrong.
  const shown = useMemo(() => entries.filter((e) => {
    if (projectId !== "all" && e.project !== projectId) return false;
    if (billableOnly && entryKind(e, projects) !== "hourly") return false;
    if (unbilledOnly && e.invoicedAt) return false;
    const day = entryDayKey(e);
    if (periodMode === "month") return day.slice(0, 7) === month;
    if (periodMode === "since") return day >= since;
    return true;
  }).sort((a, b) => (a.start || "").localeCompare(b.start || "")), [entries, projectId, billableOnly, unbilledOnly, periodMode, month, since, projects]);

  // Only Billable (hourly) time carries money; retainer and fixed-fee work
  // prints its hours with no amount, whatever rate it was logged with.
  const charged = (e: TimeEntry) => entryKind(e, projects) === "hourly";

  /** What "Mark as invoiced" would stamp: billable work in view not yet billed.
   *  Non-billable time never goes on a bill, so it is never stamped as if it had. */
  const toMark = useMemo(() => shown.filter((e) => isUnbilled(e, projects)), [shown, projects]);
  const alreadyInvoiced = shown.filter((e) => e.invoicedAt).length;

  /** Summary collapses to one row per project; the others list every entry. */
  const rows = useMemo(() => {
    if (preset !== "summary") {
      return shown.map((e) => ({
        key: e.id,
        date: entryDayKey(e),
        project: projects.find((p) => p.id === e.project)?.name ?? "—",
        description: deriveEntryLabel(e.note, itemTitle.get(e.item ?? "")),
        activity: e.category ?? "",
        seconds: e.durationSeconds || 0,
        rate: charged(e) ? (e.rateSnapshot ?? null) : null,
      }));
    }
    const byProject = new Map<string, { seconds: number; amount: number; rates: Set<number> }>();
    for (const e of shown) {
      const name = projects.find((p) => p.id === e.project)?.name ?? "—";
      const agg = byProject.get(name) ?? { seconds: 0, amount: 0, rates: new Set<number>() };
      agg.seconds += e.durationSeconds || 0;
      if (charged(e) && e.rateSnapshot) {
        agg.amount += ((e.durationSeconds || 0) / 3600) * e.rateSnapshot;
        agg.rates.add(e.rateSnapshot);
      }
      byProject.set(name, agg);
    }
    return [...byProject].map(([name, agg]) => ({
      key: name, date: "", project: name, description: "", activity: "",
      seconds: agg.seconds,
      // A project billed at two rates in one period has no single rate to show.
      rate: agg.rates.size === 1 ? [...agg.rates][0] : null,
    }));
  }, [preset, shown, projects, itemTitle]);

  const totalSeconds = shown.reduce((n, e) => n + (e.durationSeconds || 0), 0);
  const totalAmount = shown.reduce(
    (n, e) => n + (charged(e) && e.rateSnapshot ? ((e.durationSeconds || 0) / 3600) * e.rateSnapshot : 0),
    0,
  );
  const currency = shown.find((e) => e.currency)?.currency ?? "USD";

  const periodLabel =
    periodMode === "all" ? "All time"
    : periodMode === "month" ? (months.find((m) => m.value === month)?.label ?? month)
    : `Since ${since}`;

  const project = projects.find((p) => p.id === projectId);
  const clientLabel = project
    ? (clients.find((c) => c.id === project.clientRef)?.name ?? project.client ?? project.name)
    : "All clients";

  async function markInvoiced() {
    const ids = toMark.map((e) => e.id);
    const number = invoiceNumber.trim();
    try {
      await setInvoiced.mutateAsync({ ids, invoicedAt: new Date().toISOString(), invoiceNumber: number });
      setMarking(false);
      toast(`Marked ${ids.length} ${ids.length === 1 ? "entry" : "entries"} invoiced${number ? ` on ${number}` : ""}`, {
        undo: () => void setInvoiced.mutateAsync({ ids, invoicedAt: null })
          .catch(() => toast("Could not undo — clear them from the entry editor")),
      });
    } catch {
      // Sequential writes: whatever landed before the failure stays marked,
      // and the filter will show exactly what is left.
      toast("Some entries could not be marked — what's still listed is still unbilled");
    }
  }

  function choosePreset(next: Preset) {
    setPreset(next);
    setFields(PRESETS[next].fields);
  }

  function exportCsv() {
    if (shown.length === 0) { toast("Nothing in this selection"); return; }
    const q = (s: string) => `"${s.replace(/"/g, '""')}"`;
    const header = ["Date", "Project", "Description", "Activity", "Hours", "Rate", "Amount", "Currency"];
    const lines = rows.map((r) => [
      q(r.date), q(r.project), q(r.description), q(r.activity),
      (r.seconds / 3600).toFixed(2),
      r.rate === null ? "" : String(r.rate),
      r.rate === null ? "" : ((r.seconds / 3600) * r.rate).toFixed(2),
      q(currency),
    ].join(","));
    const url = URL.createObjectURL(new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoice-attachment-${periodLabel.replace(/[^\w]+/g, "-").toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast(`Exported ${rows.length} ${rows.length === 1 ? "line" : "lines"}`);
  }

  const columns = [
    fields.date && "Date",
    fields.project && "Project",
    fields.description && "Description",
    fields.activity && "Activity",
    fields.duration && "Hours",
    fields.rate && "Rate",
    fields.amount && "Amount",
  ].filter(Boolean) as string[];

  return (
    <div className="print-sheet" style={{
      position: "fixed", inset: 0, zIndex: 70, overflowY: "auto",
      background: "var(--bg)",
    }}>
      {/* -- Controls --------------------------------------------------------- */}
      <div className="no-print" style={{
        position: "sticky", top: 0, zIndex: 1, background: "var(--surface)",
        borderBottom: "1px solid var(--line)", padding: "14px 24px",
        display: "flex", flexDirection: "column", gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 16, fontWeight: "var(--fw-title)" }}>Invoice attachment</span>
          <span style={{ fontSize: 12, color: "var(--muted)" }}>
            {shown.length} {shown.length === 1 ? "entry" : "entries"} · {hours(totalSeconds)} h
            {totalAmount > 0 && ` · ${money(totalAmount, currency)}`}
          </span>
          <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button onClick={exportCsv} style={ghost}>Export CSV</button>
            <button onClick={() => window.print()} style={primary}>Print / Save PDF</button>
            <button
              onClick={() => setMarking((v) => !v)}
              disabled={toMark.length === 0}
              title={toMark.length === 0 ? "Nothing billable and unbilled in this selection" : "Record that these hours have been invoiced"}
              style={{ ...ghost, ...(toMark.length === 0 ? { opacity: 0.5, cursor: "not-allowed" } : null) }}
            >Mark as invoiced</button>
            <button onClick={onClose} style={ghost}>Close</button>
          </span>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {(Object.keys(PRESETS) as Preset[]).map((key) => (
            <button
              key={key}
              onClick={() => choosePreset(key)}
              title={PRESETS[key].hint}
              style={{ ...pill, ...(preset === key ? pillOn : null) }}
            >{PRESETS[key].label}</button>
          ))}
          <span style={{ fontSize: 11.5, color: "var(--muted)", flex: "1 1 240px" }}>{PRESETS[preset].hint}</span>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select value={periodMode} onChange={(e) => setPeriodMode(e.target.value as PeriodMode)} aria-label="Period" style={input}>
            <option value="month">A month</option>
            <option value="since">Since a date</option>
            <option value="all">All time</option>
          </select>
          {periodMode === "month" && (
            <select value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Month" style={input}>
              {months.length === 0 && <option value={month}>No entries yet</option>}
              {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          )}
          {periodMode === "since" && (
            <input type="date" value={since} onChange={(e) => setSince(e.target.value)} aria-label="Since" style={input} />
          )}
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} aria-label="Project" style={input}>
            <option value="all">All projects</option>
            {projects.filter((p) => p.kind !== "inbox").map((p) => (
              <option key={p.id} value={p.id}>{p.client ? `${p.name} · ${p.client}` : p.name}</option>
            ))}
          </select>
          <button onClick={() => setBillableOnly((v) => !v)} style={{ ...pill, ...(billableOnly ? pillOn : null) }}>
            <span style={{ display: "inline-block", width: 11 }}>{billableOnly ? "✓" : ""}</span>Billable only
          </button>
          <button onClick={() => setUnbilledOnly((v) => !v)} style={{ ...pill, ...(unbilledOnly ? pillOn : null) }}>
            <span style={{ display: "inline-block", width: 11 }}>{unbilledOnly ? "✓" : ""}</span>Not yet invoiced
          </button>
          {/* Printed on the attachment and stamped on the entries when you mark
              them — so the timesheet and the record carry the same number. */}
          <input
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            placeholder="Invoice # (optional)"
            aria-label="Invoice number"
            style={{ ...input, width: 160 }}
          />
          {!unbilledOnly && alreadyInvoiced > 0 && (
            <span style={{ fontSize: 11.5, color: "var(--muted)" }}>{alreadyInvoiced} already invoiced in this selection</span>
          )}
        </div>

        {marking && toMark.length > 0 && (
          <div style={{
            display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
            padding: "10px 12px", borderRadius: 10, background: "var(--accentTint2)", border: "1px solid var(--accentLine)",
          }}>
            <span style={{ fontSize: 12.5, color: "var(--ink2)" }}>
              Mark {toMark.length} billable {toMark.length === 1 ? "entry" : "entries"} ({hours(toMark.reduce((n, e) => n + (e.durationSeconds || 0), 0))} h) as invoiced
            </span>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>
              {invoiceNumber.trim() ? `on invoice ${invoiceNumber.trim()}` : "— no invoice number (you can add one beside the filters)"}
            </span>
            <button onClick={() => void markInvoiced()} disabled={setInvoiced.isPending} style={primary}>
              {setInvoiced.isPending ? "Marking…" : "Mark invoiced"}
            </button>
            <button onClick={() => setMarking(false)} style={ghost}>Cancel</button>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {FIELD_LABELS.map(([key, label]) => (
            <label key={key} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--ink3)" }}>
              <input
                type="checkbox"
                checked={fields[key]}
                onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.checked }))}
              />
              {label}
            </label>
          ))}
        </div>
      </div>

      {/* -- The document ----------------------------------------------------- */}
      <div style={{ padding: "28px 24px 60px", display: "flex", justifyContent: "center" }}>
        <div className="prose print-page" style={{
          ...card, width: 820, maxWidth: "100%", ...reportPaper,
          color: "var(--ink)", boxShadow: "var(--shadowCard)",
        }}>
          <ReportMasthead
            kind="Timesheet"
            showBrand={fields.businessDetails}
            title={fields.clientDetails ? clientLabel : "Time entries"}
            subtitle={fields.clientDetails && project ? project.name : undefined}
            meta={[
              ["Period", fields.clientDetails && periodLabel],
              ["Invoice", invoiceNumber.trim()],
              ["Prepared", new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })],
            ]}
          />

          {rows.length === 0 ? (
            <div style={{ padding: "40px 0", textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
              Nothing matches this selection.
            </div>
          ) : (
            <table style={reportTable}>
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c} style={{
                      ...reportTh, textAlign: c === "Hours" || c === "Rate" || c === "Amount" ? "right" : "left",
                    }}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key}>
                    {fields.date && <td style={cell}>{r.date}</td>}
                    {fields.project && <td style={cell}>{r.project}</td>}
                    {fields.description && <td style={cell}>{r.description}</td>}
                    {fields.activity && <td style={cell}>{r.activity}</td>}
                    {fields.duration && <td style={{ ...cell, textAlign: "right" }} className="mono">{hours(r.seconds)}</td>}
                    {fields.rate && <td style={{ ...cell, textAlign: "right" }} className="mono">{r.rate === null ? "—" : money(r.rate, currency)}</td>}
                    {fields.amount && (
                      <td style={{ ...cell, textAlign: "right" }} className="mono">
                        {r.rate === null ? "—" : money((r.seconds / 3600) * r.rate, currency)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {fields.subtotals && rows.length > 0 && (
            <div style={{
              display: "flex", justifyContent: "flex-end", gap: 28,
              marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--line2)", fontSize: 13,
            }}>
              <span><span style={{ color: "var(--muted)" }}>Total hours </span>
                <strong className="mono">{hours(totalSeconds)}</strong></span>
              {/* Only when the Amount column is shown. The tracker prints a
                  billed total even on the hours-only preset, which puts a
                  dollar figure on the one document whose point is not to
                  carry one. */}
              {fields.amount && totalAmount > 0 && (
                <span><span style={{ color: "var(--muted)" }}>Total </span>
                  <strong className="mono">{money(totalAmount, currency)}</strong></span>
              )}
            </div>
          )}

          {fields.footerNote && <ReportFooter />}
        </div>
      </div>
    </div>
  );
}

const cell = reportTd;
const input: React.CSSProperties = {
  padding: "7px 10px", borderRadius: 8, fontSize: 12.5,
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
};
const pill: React.CSSProperties = {
  padding: "7px 13px", borderRadius: 999, fontSize: 12.5, cursor: "pointer",
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)",
  display: "inline-flex", alignItems: "center", gap: 3,
};
const pillOn: React.CSSProperties = {
  background: "var(--accentTint)", border: "1px solid var(--accentLine)", color: "var(--accent)",
};
const primary: React.CSSProperties = {
  padding: "7px 14px", borderRadius: 999, border: "none",
  background: "var(--accent)", color: "var(--onAccent)", fontSize: 12.5, fontWeight: 600, cursor: "pointer",
};
const ghost: React.CSSProperties = {
  padding: "7px 14px", borderRadius: 999, fontSize: 12.5, cursor: "pointer",
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink2)",
};
